/**
 * use-landing-templates / media-sources / product copy — 落地页只读清单与文案
 *
 * 模板：GET /landing-templates（生成器未接入则 503，页面 fail-closed）。
 * 渠道：GET /landing-media-sources。
 * 文案：GET/PUT/DELETE /landing-products/:id/copy/:language。
 */

import type {
  LandingCopy,
  LandingCopyDto,
  LandingCopyProvisioning,
  PutLandingCopyRequest,
} from '@novel-admin/shared';
import { apiRequest } from '~/api/client';

export type LandingTemplateItem = {
  id: string;
  name?: string;
  needs_image?: boolean;
  desc?: string;
};

export type LandingTemplateOption = {
  id?: string;
  name?: string;
  requires?: string[];
  desc?: string;
};

/**
 * 可编辑文案键的一条描述。生成器 `export.py --templates` 的 `copy.keys[]` 是**对象数组**
 * （`{key, name, scope}`），不是键名字符串数组 —— 曾按 `string[]` 声明，页面把整个对象
 * 塞进 JSX，React 报 #31「object with keys {key, name, scope}」整页白屏。
 *
 * `scope` = 该键属于哪一层（`base` 恒生效 / `topbar` 仅开顶栏时生效），用于在表格里标注。
 */
export type LandingCopyKeyItem = {
  key: keyof LandingCopy;
  name?: string;
  scope?: string;
};

export type LandingTemplatesPayload = {
  copy_provisioning?: LandingCopyProvisioning[];
  templates: LandingTemplateItem[];
  options?: LandingTemplateOption[];
  copy?: { keys?: LandingCopyKeyItem[]; defaults?: Record<string, Partial<LandingCopy>> };
};

export const landingTemplatesApi = {
  retryCopy: (language: string): Promise<{ queued: true }> =>
    apiRequest({
      method: 'POST',
      path: `/api/v1/landing-templates/copy-provisioning/${encodeURIComponent(language)}/retry`,
    }),
  get: (): Promise<LandingTemplatesPayload> =>
    apiRequest<LandingTemplatesPayload>({ method: 'GET', path: '/api/v1/landing-templates' }),
};

export const landingMediaSourcesApi = {
  list: (): Promise<{ media_sources: string[] }> =>
    apiRequest<{ media_sources: string[] }>({
      method: 'GET',
      path: '/api/v1/landing-media-sources',
    }),
};

export const landingProductCopyApi = {
  get: (productId: string, language: string): Promise<LandingCopyDto> =>
    apiRequest<LandingCopyDto>({
      method: 'GET',
      path: `/api/v1/landing-products/${productId}/copy/${language}`,
    }),
  put: (
    productId: string,
    language: string,
    body: PutLandingCopyRequest,
  ): Promise<LandingCopyDto> =>
    apiRequest<LandingCopyDto>({
      method: 'PUT',
      path: `/api/v1/landing-products/${productId}/copy/${language}`,
      body,
    }),
  remove: (productId: string, language: string): Promise<LandingCopyDto> =>
    apiRequest<LandingCopyDto>({
      method: 'DELETE',
      path: `/api/v1/landing-products/${productId}/copy/${language}`,
    }),
};
